import { Link } from "react-router-dom";
import { Send, MessageCircle } from "lucide-react";

import Logo from "@/components/Logo";
import { ROUTES } from "@/constants/routes";
import { SPACING } from "@/constants/spacing";
import { trackExternalLink } from "@/utils/analytics";

// Simple SVG icons to replace deprecated lucide-react brand icons
const TwitterIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />
  </svg>
);

const YoutubeIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z" />
    <path d="m9.75 15.02 5.75-3.27-5.75-3.27v6.54z" />
  </svg>
);

/**
 * Footer - Global site footer component
 *
 * Features:
 * - Responsive grid layout (1/2/4 columns)
 * - Navigation links organized by category (Product, Company, Resources)
 * - Social media links with accessibility labels
 * - Legal links (Privacy Policy, Terms of Service)
 * - Copyright notice
 * - Accessible navigation with ARIA labels
 */
export default function Footer() {
  return (
    <footer
      id="footer"
      className="relative z-10 w-full bg-brand-dark border-t border-border-light"
      role="contentinfo"
    >
      <div
        className="w-full py-12 sm:py-14 md:py-16"
        style={{
          paddingLeft: SPACING.contentPadding,
          paddingRight: SPACING.contentPadding,
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12 text-center md:text-left">
          {/* Logo and Description */}
          <div className="lg:col-span-1">
            <Link to={ROUTES.HOME} className="inline-block mb-6">
              <Logo
                gradientId="paint0_radial_footer"
                className="h-36 w-auto"
                width={576}
                height={144}
                loading="eager"
              />
            </Link>
            <p className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px]">
              A Launch OS that enforces standards, verifies execution, and generates
              decision-ready intelligence.
            </p>
          </div>

          {/* Product Links */}
          <div>
            <h2 className="font-sans font-medium text-[18px] leading-[24px] text-white tracking-[-0.18px] mb-6">
              Product
            </h2>
            <nav
              aria-label="Product links"
              className="flex flex-col gap-4 items-center md:items-start"
            >
              <Link
                to={ROUTES.HOME}
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                Home
              </Link>
              <Link
                to={ROUTES.ECOSYSTEM}
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                Ecosystem
              </Link>
              <Link
                to={ROUTES.AI_ENGINE}
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                AI Engine
              </Link>
              <Link
                to={ROUTES.TOKENOMICS}
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                Tokenomics
              </Link>
              <Link
                to={ROUTES.TRACTION}
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                Traction
              </Link>
              <Link
                to={ROUTES.ELEVATE_TO_ALPHA}
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                Elevate to Alpha
              </Link>
              <Link
                to={ROUTES.ROADMAP}
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                Roadmap
              </Link>
              <Link
                to={ROUTES.USER_JOURNEYS}
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                User Journeys
              </Link>
            </nav>
          </div>

          {/* Company Links */}
          <div>
            <h2 className="font-sans font-medium text-[18px] leading-[24px] text-white tracking-[-0.18px] mb-6">
              Company
            </h2>
            <nav
              aria-label="Company links"
              className="flex flex-col gap-4 items-center md:items-start"
            >
              <Link
                to={ROUTES.ABOUT_US}
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                About Us
              </Link>
              <Link
                to={ROUTES.OUR_TEAM}
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                Our Team
              </Link>
              <Link
                to={ROUTES.REGULATORY_COMPLIANCE}
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                Regulatory Compliance
              </Link>
              <Link
                to={ROUTES.SOCIAL_IMPACT_COLLECTIVE}
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                Social Impact Collective
              </Link>
              <Link
                to={ROUTES.CAREERS}
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                Careers
              </Link>
              <Link
                to={ROUTES.PRESS_KIT}
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                Press Kit
              </Link>
              <Link
                to={ROUTES.LEGAL}
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                Legal
              </Link>
              <Link
                to={ROUTES.CONTACT}
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                Contact
              </Link>
              <Link
                to={ROUTES.BLOG}
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                Blog
              </Link>
              <Link
                to={ROUTES.FAQ}
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                FAQ
              </Link>
            </nav>
          </div>

          {/* Resources Links */}
          <div>
            <h2 className="font-sans font-medium text-[18px] leading-[24px] text-white tracking-[-0.18px] mb-6">
              Documents
            </h2>
            <nav
              aria-label="Documents"
              className="flex flex-col gap-4 items-center md:items-start"
            >
              <Link
                to="https://docsend.com/view/3yvihutsxhkc27b5"
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                White Paper
              </Link>
              <Link
                to="https://docsend.com/view/85fbygt3jb4sybby"
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                Token Economics
              </Link>
              <Link
                to="https://docsend.com/view/w83xefp22eveh39r"
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                Founding Team
              </Link>
              <Link
                to="https://docsend.com/view/evt2kn95i257sqif"
                className="font-sans font-normal text-[16px] leading-[24px] text-text-secondary tracking-[-0.16px] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
              >
                User Journeys
              </Link>
            </nav>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="pt-8 border-t border-border-light flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="font-sans font-normal text-[16px] leading-[20px] text-[rgba(255,255,255,0.65)] tracking-[-0.14px]">
            © 2026 Becoming Alpha. All rights reserved.
          </p>

          {/* Social Links */}
          <div className="flex gap-4">
            <a
              href="https://twitter.com/becomingalpha"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-border-light flex items-center justify-center hover:bg-border-medium transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark"
              aria-label="Twitter"
              onClick={() =>
                trackExternalLink("https://twitter.com/becomingalpha", "Twitter")
              }
            >
              <TwitterIcon className="w-5 h-5 text-white" />
            </a>
            <a
              href="https://t.me/becomingalpha"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-border-light flex items-center justify-center hover:bg-border-medium transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark"
              aria-label="Telegram"
              onClick={() => trackExternalLink("https://t.me/becomingalpha", "Telegram")}
            >
              <Send className="w-5 h-5 text-white" />
            </a>
            <a
              href="https://discord.gg/becomingalpha"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-border-light flex items-center justify-center hover:bg-border-medium transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark"
              aria-label="Discord"
              onClick={() =>
                trackExternalLink("https://discord.gg/becomingalpha", "Discord")
              }
            >
              <MessageCircle className="w-5 h-5 text-white" />
            </a>
            <a
              href="https://youtube.com/@becomingalpha"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-border-light flex items-center justify-center hover:bg-border-medium transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark"
              aria-label="YouTube"
              onClick={() =>
                trackExternalLink("https://youtube.com/@becomingalpha", "YouTube")
              }
            >
              <YoutubeIcon className="w-5 h-5 text-white" />
            </a>
          </div>

          {/* Legal Links */}
          <div className="flex flex-wrap gap-4 md:gap-6 justify-center md:justify-start">
            <Link
              to={ROUTES.PRIVACY_POLICY}
              className="font-sans font-normal text-[16px] leading-[20px] text-[rgba(255,255,255,0.65)] tracking-[-0.14px] hover:text-[rgba(255,255,255,0.85)] transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
            >
              Privacy Policy
            </Link>
            <Link
              to={ROUTES.TERMS_OF_SERVICE}
              className="font-sans font-normal text-[16px] leading-[20px] text-[rgba(255,255,255,0.65)] tracking-[-0.14px] hover:text-[rgba(255,255,255,0.85)] transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-dark rounded"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
